import { useSyncExternalStore } from "react";

export type WaveAppearanceMode = "system" | "light" | "dark";

const STORAGE_KEY = "wave.appearance.mode";

const MODE_LABELS: Record<WaveAppearanceMode, string> = {
  system: "Sistema",
  light: "Chiaro",
  dark: "Scuro",
};

export const WAVE_APPEARANCE_MODES: WaveAppearanceMode[] = ["system", "light", "dark"];

export function waveAppearanceLabel(mode: WaveAppearanceMode): string {
  return MODE_LABELS[mode];
}

function loadMode(): WaveAppearanceMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    /* ignore */
  }
  return "system";
}

let currentMode: WaveAppearanceMode = loadMode();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function applyDomTheme(mode: WaveAppearanceMode): void {
  const root = document.documentElement;
  if (mode === "system") {
    root.removeAttribute("data-wave-theme");
  } else {
    root.setAttribute("data-wave-theme", mode);
  }
}

export function applyWaveTheme(mode: WaveAppearanceMode = currentMode): void {
  currentMode = mode;
  applyDomTheme(mode);
}

export function getWaveThemeMode(): WaveAppearanceMode {
  return currentMode;
}

export function setWaveThemeMode(mode: WaveAppearanceMode): void {
  if (currentMode === mode) return;
  currentMode = mode;
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
  applyDomTheme(mode);
  notify();
}

export function subscribeWaveTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Call before React render to avoid theme flash. */
export function initWaveTheme(): void {
  currentMode = loadMode();
  applyDomTheme(currentMode);
}

export function useWaveTheme(): {
  mode: WaveAppearanceMode;
  setMode: (mode: WaveAppearanceMode) => void;
} {
  const mode = useSyncExternalStore(subscribeWaveTheme, getWaveThemeMode, () => "system" as WaveAppearanceMode);
  return { mode, setMode: setWaveThemeMode };
}
